import {
  addDays,
  addMonths,
  startOfDay,
  startOfMonth,
  type DateRange,
} from '../../molecules/CalendarMonth/calendar';

/**
 * A row in Figma's `Pre-set ranges` sidebar, and an option in the single-date
 * menu's `Select`. `getValue` is what the row applies; `None` and `All time`
 * both return an empty range — see `NONE_PRESET_ID` for how they differ.
 */
/**
 * Figma's first row, `None` (added 2026-09-21): no date filter at all. It
 * applies the empty range, which is also what `Clear selection` leaves, and it
 * is the row that reads active while nothing is selected.
 *
 * `All time` returns the same empty range here because this generic list has no
 * earliest date to start from; a caller that knows one (the app knows its
 * corpus) overrides that row with real bounds, and the two then read apart —
 * `All time` on the trigger, the placeholder for `None`.
 *
 * Dual-date only: the single-date `Select` in Figma does not list it, so
 * `DatePickerMenu` drops it there by this id.
 */
export const NONE_PRESET_ID = 'none';

export type DatePickerPreset = {
  id: string;
  label: string;
  getValue: () => DateRange;
};

const endOfMonth = (date: Date) => addDays(addMonths(date, 1), -1);

/**
 * The ten rows Figma's `Leading content` (123:2193) lists, in its order.
 *
 * `reference` is "now" — passed in rather than read from the clock so stories
 * and parity runs are reproducible. Weeks start on Monday, matching the grid.
 */
export function defaultDatePickerPresets(reference: Date = new Date()): DatePickerPreset[] {
  const today = startOfDay(reference);
  const weekStart = addDays(today, -((today.getDay() + 6) % 7));
  const monthStart = startOfMonth(today);
  const yearStart = new Date(today.getFullYear(), 0, 1);

  return [
    { id: NONE_PRESET_ID, label: 'None', getValue: () => ({ start: null, end: null }) },
    { id: 'today', label: 'Today', getValue: () => ({ start: today, end: today }) },
    {
      id: 'yesterday',
      label: 'Yesterday',
      getValue: () => ({ start: addDays(today, -1), end: addDays(today, -1) }),
    },
    {
      id: 'this-week',
      label: 'This week',
      getValue: () => ({ start: weekStart, end: addDays(weekStart, 6) }),
    },
    {
      id: 'last-week',
      label: 'Last week',
      getValue: () => ({ start: addDays(weekStart, -7), end: addDays(weekStart, -1) }),
    },
    {
      id: 'this-month',
      label: 'This month',
      getValue: () => ({ start: monthStart, end: endOfMonth(monthStart) }),
    },
    {
      id: 'last-month',
      label: 'Last month',
      getValue: () => {
        const previous = addMonths(monthStart, -1);
        return { start: previous, end: endOfMonth(previous) };
      },
    },
    {
      id: 'this-year',
      label: 'This year',
      getValue: () => ({ start: yearStart, end: new Date(today.getFullYear(), 11, 31) }),
    },
    {
      id: 'last-year',
      label: 'Last year',
      getValue: () => ({
        start: new Date(today.getFullYear() - 1, 0, 1),
        end: new Date(today.getFullYear() - 1, 11, 31),
      }),
    },
    { id: 'all-time', label: 'All time', getValue: () => ({ start: null, end: null }) },
  ];
}
